import type { GlossarySection } from "@thalamos/common";

export const dataGlossarySections: GlossarySection[] = [
  {
    heading: "Incidents",
    variant: "standard",
    intro:
      'This section defines what an "incident" is in MHForms and the key variants (S136, MCA, Voluntary), including how live vs completed workflow states are distinguished.',
    rows: [
      {
        term: "Incident",
        definition:
          "A care pathway in which police officers interact with an individual undergoing a mental health crisis.",
        dataContext:
          "An incident is the full process and data input in MHForms (with additional data in eMHA for the outcome form if applicable). An incident is created when the police officer begins the form.",
        recordedOn: "Police Form; Outcome Form",
      },
      {
        term: "S136 Incident",
        definition: "An incident in which Section 136 powers are used.",
        dataContext:
          'Incidents where the officer initiates a Section 136 form by selecting "Section 136 MHA" in Incident Triage.',
        recordedOn: "Police Form",
      },
      {
        term: "MCA Incident",
        definition:
          "An incident in which a Mental Capacity Act assessment takes place as part of the police intervention.",
        dataContext:
          'Incidents where the officer initiates a Section 1-6 Mental Capacity Act assessment form by selecting "Section 1-6 MCA" in Incident Triage and the subsequent incident which includes this action.',
        recordedOn: "Police Form",
      },
      {
        term: "Incident Status",
        definition:
          "The current point an incident has reached in the workflow. Whether it is still ongoing (and which step it has reached) or has been completed and closed/ended.",
        dataContext:
          "A MHForms generated label showing the current workflow stage of an incident. Statuses can be one of the following: Unknown; In progress; Awaiting arrival; Arrived at a place of safety (s136); Arrived at the emergency department (MCA); Form shared; NHS form accepted; Police presence requested; Police presence not requested; Ended",
        recordedOn: "Police Form",
      },
      {
        term: "Completed Incident",
        definition:
          "An incident which has been closed and marked as ended by the police user.",
        dataContext:
          'An incident is marked as ended when an officer selects "Finish Form" and enters the subsequent place of safety leave date and time, and the outcome. A timestamp is assigned to the incident using the date/time the individual was detained by the police.',
        recordedOn: "Police Form",
      },
      {
        term: "Live Incident",
        definition:
          "An incident which is currently ongoing and has not been marked as ended.",
        dataContext:
          "An incident is live when it has an open form context created by an officer before it is finished. The incident status is anything other than ended. A timestamp is assigned to the incident using the date/time the individual was detained by the police.",
        recordedOn: "Police Form",
      },
    ],
  },
  {
    heading: "Canonical Reporting Populations",
    variant: "notes",
    intro:
      "This section sets the standard reporting populations used across the dashboards. These definitions make denominators explicit and ensure metrics are comparable across pages.",
    rows: [
      {
        term: "Combined Reporting Populations",
        definition: "",
        dataContext: "",
        notes: "",
        isGroupHeader: true,
      },
      {
        term: "Incident Reporting Base Population",
        definition:
          "All incidents regardless of whether they're still in progress or ended.",
        dataContext: "Form is created.",
        notes: "",
      },
      {
        term: "Completed Incidents",
        definition:
          "Incidents that have been completed and closed by the police user.",
        dataContext: "The incident status is ended.",
        notes: "",
      },
      {
        term: "Live Incidents",
        definition: "Incidents that are still ongoing and not yet closed.",
        dataContext: "The incident status is anything other than ended.",
        notes: "",
      },
      {
        term: "S136 Reporting Populations",
        definition: "",
        dataContext: "",
        notes: "",
        isGroupHeader: true,
      },
      {
        term: "S136 Reporting Base Population",
        definition:
          "All incidents that are Section 136 incidents regardless of whether they're still in progress or ended.",
        dataContext:
          "A s136 Incident, applying the Global reporting exclusions.",
        notes: "",
      },
      {
        term: "Completed S136 Incidents",
        definition:
          "Section 136 incidents that have been completed and closed by the police user.",
        dataContext:
          "S136 reporting base population AND incident status is ended.",
        notes: "",
      },
      {
        term: "Live S136 incidents",
        definition:
          "Section 136 incidents that are still ongoing and not yet closed.",
        dataContext:
          "S136 reporting base population AND incident status is anything other than ended.",
        notes: "",
      },
      {
        term: "Outcome-recorded S136 incidents",
        definition:
          "Completed Section 136 incidents where a health outcome has been recorded.",
        dataContext:
          "Completed S136 incidents which has an associated complete Outcome Form.",
        notes: "",
      },
      {
        term: "Extended S136 incidents",
        definition: "Section 136 incidents that have been extended.",
        dataContext:
          "S136 reporting base population AND the incident has been extended with an extension form.",
        notes: "",
      },
      {
        term: "MCA Reporting Populations",
        definition: "",
        dataContext: "",
        notes: "",
        isGroupHeader: true,
      },
      {
        term: "MCA Reporting Base Population",
        definition:
          "All incidents that are MCA incidents regardless of whether they're still in progress or ended.",
        dataContext:
          "An MCA Incident applying the Global reporting exclusions.",
        notes: "",
      },
      {
        term: "Completed MCA Incidents",
        definition:
          "MCA incidents that have been completed and closed by the police user.",
        dataContext:
          "MCA reporting base population AND incident status is ended.",
        notes: "",
      },
      {
        term: "Live MCA incidents",
        definition: "MCA incidents that are still ongoing and not yet closed.",
        dataContext:
          "MCA reporting base population AND incident status is anything other than ended.",
        notes: "",
      },
      {
        term: "Global Reporting Exclusions",
        definition: "",
        dataContext: "",
        notes: "",
        isGroupHeader: true,
      },
      {
        term: "Started in Error",
        definition:
          "Exclude incidents that were ended because they were started in error so they don't skew reporting.",
        dataContext:
          'Exclude incidents where the form was ended and the reason given was "started in error", unless explicitly stated otherwise. Applies across all metrics by default.',
        notes: "",
      },
    ],
  },
  {
    heading: "Time Periods",
    variant: "standard",
    intro:
      "This section defines how metrics are assigned to time (day/week/month/year) and standardises common reporting windows (MTD, YTD, rolling 30 days, previous period, equivalent period last year). It also clarifies the difference between incident time attribution (typically detention time) and action time attribution (the timestamp of the action being measured).\n\nThe reporting windows are defined by the dashboard's inbuilt time filtering functionality and are presented here for context and user convenience.",
    rows: [
      {
        term: "Time Attribution Rule",
        definition:
          "Rule for which timestamp is used to assign incidents/actions to a day/week/month/year.",
        dataContext:
          "An incident (or action) is assigned to a day/week/month/year using the following timestamp rules:\n\nCompleted incidents: assigned using the date/time the individual was detained by the police.\n\nLive incidents: assigned using the date/time the individual was detained by the police.\n\nAction metrics: assigned using the timestamp recorded for the specific action (e.g., Arrived at POS date/time, Police leave POS date/time).",
        recordedOn: "Police Form; Outcome Form",
      },
      {
        term: "Time Series",
        definition:
          "Trend view showing how metrics change over time, displayed as a sequence of time periods (monthly by default).",
        dataContext:
          "Values are aggregated into consecutive time buckets using the dashboard's time attribution rule. Grouping is by month by default, with optional week/day views where available.",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Current <period> (to-date)",
        definition:
          "From the start of the current calendar <period> up to now (to-date).",
        dataContext:
          'Calculated using start time of <period> to "now" timestamp.',
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Current day",
        definition: "From the start of the current day up to now.",
        dataContext: 'Calculated using day start (00:00) to "now" timestamp.',
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Current week",
        definition:
          "From the start of the current week up to now (week-to-date).",
        dataContext:
          'Calculated using week start (Monday 00:00) to "now" timestamp.',
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Current month",
        definition:
          "From the start of the current calendar month up to now (month-to-date).",
        dataContext:
          'Calculated using month start (1st day 00:00) to "now" timestamp.',
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Current year",
        definition:
          "From the start of the current calendar year up to now (year-to-date).",
        dataContext: 'Calculated using 1 Jan 00:00 to "now" timestamp.',
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Last <period> (rolling window)",
        definition:
          "A rolling window ending now, with length equal to the <period>.",
        dataContext: "Time between now - <period> up to now.",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Last day",
        definition: "The most recent rolling day to now.",
        dataContext: "Time between now and 24 hours ago.",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Last week",
        definition: "The most recent rolling week to now.",
        dataContext: "Time between now and 7 days ago.",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Last month",
        definition: "The most recent month day to now.",
        dataContext: "Time between now and the same day of last month.",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Last quarter",
        definition: "The most recent rolling quarter to now.",
        dataContext: "Time between now and the same day 3 months ago.",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Last year",
        definition: "The last 365 days up to now (rolling window).",
        dataContext: "Time between now the same day 1 year ago.",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Previous <period> (last complete calendar period)",
        definition:
          "The most recent fully completed calendar <period> immediately before the current one.",
        dataContext:
          "Calculated from the start of the previous <period> to the start of the current <period>",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Previous calendar week",
        definition: "The previous completed calendar month.",
        dataContext:
          "From start of previous complete week to end of previous week (Mon 00:00 to Sun 23:59:59).",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Previous calendar month",
        definition: "The previous completed calendar month.",
        dataContext:
          "From 1st of previous complete month 00:00 to last day of last month 23:59:59.",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Previous calendar quarter",
        definition: "The previous completed calendar quarter.",
        dataContext:
          "From 1st of previous complete quarter 00:00 to last day of last quarter 23:59:59.",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Previous calendar year",
        definition: "The previous completed calendar year.",
        dataContext: "From 1 Jan last year 00:00 to 31 Dec last year 23:59:59.",
        recordedOn: "Dashboard Calculation",
      },
      {
        term: "Equivalent Period (Previous Year)",
        definition:
          "The same start and end dates shifted back exactly one calendar year.",
        dataContext:
          "Current filter start time - 365 days AND end time - 365 days",
        recordedOn: "Dashboard Calculation",
      },
    ],
  },
  {
    heading: "Place of Safety",
    variant: "standard",
    intro:
      "This section defines the health based locations that the police interact with during an incident, and related concepts in relation to the presented data. This will be Places of Safety (POS) for S136 incidents and Emergency Departments for MCA Incidents. POS/ED data is central to pathway reporting, but it is prone to input errors due to manual entry and inconsistent naming, so the definitions emphasise how sites are identified, how types are assigned, and the main data quality risks.",
    rows: [
      {
        term: "Place of Safety (POS)",
        definition:
          'The location where an individual is taken for assessment/care during a S136 incident. In the context of an MCA Incident, "Place of Safety" refers to the Emergency Department that the individual is taken for assessment/care.',
        dataContext:
          "Captured as a place name, address and postcode. Details are either auto-populated from a predefined list (via search/selection) or entered manually by the officer.",
        recordedOn: "Police Form",
      },
      {
        term: "Place of Safety Type / Site Type",
        definition:
          "A categorisation of the type of location used as the place of safety, describing the setting where the individual is taken.",
        dataContext:
          "Captured as a selected type for the recorded place of safety. The available categories are: HBPOS (s136 suite); Emergency Department; Custody; Care Home; Mental Health Unit; Private Home; Police Station; Other.\n\nSite type is a property of each individual Place of Safety, but it can also be entered by an officer. For reporting, the Place of Safety site type property takes precedence over officer-entered site type.\n\nFor MCA Incidents, the site type will always be Emergency Department.",
        recordedOn: "Police Form",
      },
      {
        term: "Emergency Department (specifically in a MCA Incident context)",
        definition:
          "The health based location where an individual is taken for assessment/care during a MCA incident.",
        dataContext:
          "Captured as a place name, address and postcode. Details are either auto-populated from a predefined list (via search/selection) or entered manually by the officer.",
        recordedOn: "Police Form",
      },
      {
        term: "Number of POS Visited",
        definition:
          "The number of distinct places of safety (S136) or emergency departments (MCA) the individual is taken to during a single incident.",
        dataContext:
          "Calculated by the distinct number of sites an officer inputs during an incident.",
        recordedOn: "Police Form",
      },
      {
        term: "First Place of Safety",
        definition:
          "The first recorded place of safety (S136) or emergency department (MCA) the individual is taken to during the incident.",
        dataContext:
          "Identified by officer input for where the patient was taken to in the first instance.",
        recordedOn: "Police Form",
      },
      {
        term: "Final Place of Safety",
        definition:
          "The last recorded place of safety (S136) or emergency department (MCA) the individual is taken to during the incident.",
        dataContext:
          "Identified by officer input as the site in which the officers ultimately left.",
        recordedOn: "Police Form",
      },
    ],
  },
  {
    heading: "Detention Process",
    variant: "standard",
    intro:
      "This section covers operational and pathway attributes that describe where and how detention and conveyance occurred. These fields provide context for interpreting timelines and performance variation.",
    rows: [
      {
        term: "Location of Police Detention",
        definition: "The location where the person was detained by the police.",
        dataContext:
          "Recorded as address and postcode and used as a latitude and longitude for reporting. Used to understand where detentions occur and to support geographic analysis of detention hotspots. Location is tied to the detention timestamp.",
        recordedOn: "MH Forms",
      },
      {
        term: "Detention in Custody",
        definition:
          "Whether the person was detained while already in police custody (as opposed to detention occurring in the community).",
        dataContext: "Input by officer = Yes/No",
        recordedOn: "Police Form",
      },
      {
        term: "Method of Transport",
        definition:
          "The transport method used to convey the person to the associated Place of Safety.",
        dataContext:
          "Selected by the officer from the following options: Ambulance; Police Vehicle; Other Health Vehicle; Escorted (Walked); Mental Health Ambulance; Already at POS; Other",
        recordedOn: "Police Form",
      },
      {
        term: "Reason police vehicle used to transport",
        definition:
          "The officer-stated reason a police vehicle was used to transport the patient to a Place of Safety.",
        dataContext:
          "Only applicable where Method of Transport = Police Vehicle. Options are: Ambulance Not Available in Time; Ambulance Crew Refused; Risk Assessment (Behaviour); Ambulance Not Requested; Ambulance Retasked; Other (please specify)",
        recordedOn: "Police Form",
      },
      {
        term: "Force used by police",
        definition:
          "Whether police force/restraint was used during the incident.",
        dataContext:
          "Used for safety and risk context. Input by officer = Yes/No",
        recordedOn: "Police Form",
      },
    ],
  },
  {
    heading: "HCP Consultation",
    variant: "standard",
    intro:
      "This section defines whether and how healthcare professionals were consulted during the incident, and what advice was given. These metrics are used to understand the role of clinical input in decision-making and pathway selection, so the definitions focus on when the questions apply (conditional logic) and how they should be reported.",
    rows: [
      {
        term: "Healthcare professional consulted",
        definition:
          "Whether the officer consulted a healthcare professional / clinical advice service as part of the incident.",
        dataContext:
          "Used to identify incidents where clinical input was sought prior to or during conveyance/arrival. Input by officer = Yes/No",
        recordedOn: "Police Form",
      },
      {
        term: "Reason a healthcare professional not consulted",
        definition:
          "The officer-reported reason it was not practicable to consult a healthcare professional when no consultation occurred.",
        dataContext:
          'Only applicable when Healthcare professional consulted = No. Used for "Reason not consulted" charts.\n\nBTP Options: Immediate Need for Care/Control; No Answer/Reply; Did Not Consider It; Not Sure Who to Call; Not Deemed Necessary.\n\nMPS and CoLP Options: Patient is Violent; Immediate Restraint Needed; Single Crewed/Solo Patrol; Poor Connectivity; No Answer/Reply.\n\nShared Options: Other',
        recordedOn: "Police Form",
      },
    ],
  },
  {
    heading: "Demographics",
    variant: "standard",
    intro:
      'This section defines the demographic fields used across the dashboards to describe the population subject to Section 136 and MCA Incidents. These fields support breakdowns by absolute count, and where reliable denominators exist, rate calculations (e.g., detentions per 100k). Demographic metrics should clearly state whether they use raw counts or population-standardised rates, and whether geography is attributed using detention location or place of safety location (initially it will be detention location only).\n\nBecause demographic data is entered operationally and may be incomplete, all demographic charts will handle missing/invalid values consistently (typically by grouping into "Unknown") and, where relevant, include a completeness indicator elsewhere in the dashboard (e.g., % populated).',
    rows: [
      {
        term: "Age Group",
        definition:
          "The person's age at the time of detention, grouped into standard bands for reporting.",
        dataContext:
          "Used for demographic breakdown charts and normalised rates (per 100k) where population denominators exist. Derived from DOB, age at detention calculated, and bucketed into aggregated groups.",
        recordedOn: "Police Form",
      },
      {
        term: "Gender",
        definition:
          "The person's self-identified gender as recorded by the officer.",
        dataContext:
          "Used for demographic breakdown charts. Options: Male; Female; Non-binary; Prefer not to say",
        recordedOn: "Police Form",
      },
      {
        term: "Ethnicity Group",
        definition:
          "Aggregated ethnicity grouping aligned to reporting standards (i.e. Asian, Black, Mixed, White, Other, Unknown).",
        dataContext:
          'Derived primarily from Self Defined Ethnicity (SDE). If missing / "Prefer not to say" then use Ethnic Appearance (EA) as a fallback where defined. Aggregated groups: Asian, Asian British or Asian Welsh; Black, Black British, Black Welsh, Caribbean or African; Mixed or Multiple ethnic groups; White; Other ethnic group; Unknown',
        recordedOn: "Police Form",
      },
      {
        term: "SDE code",
        definition:
          "Self Defined Ethnicity code/value recorded for the person.",
        dataContext:
          "Used for detailed ethnicity breakdowns and as the primary source for Ethnicity Group aggregation.",
        recordedOn: "Police Form",
      },
      {
        term: "EA code",
        definition:
          "Ethnic Appearance code/value (IC codes) recorded for the person.",
        dataContext:
          'Used for an "ethnic appearance" breakdown tab and as a fallback for Ethnicity Group when SDE is missing/invalid.',
        recordedOn: "Police Form",
      },
    ],
  },
  {
    heading: "Compliance",
    variant: "standard",
    intro:
      "This section defines the terminology and inputs regarding police presence and the different inputs by different user types.\n\nThis section applies to S136 Incidents only.",
    rows: [
      {
        term: "Police Presence Requested",
        definition:
          "Whether health services requested the police to remain present at the place of safety (or requested additional police presence) as part of managing risk/safety during the incident.",
        dataContext:
          "Input from the health user once the incident is shared with them by an officer. Used to report the proportion of incidents where police presence was requested, and (where available) whether the request was accepted/fulfilled.",
        recordedOn: "eMHA",
      },
      {
        term: "Reason for Requesting Police Presence",
        definition:
          "The reason health services give for requesting police presence at the place of safety.",
        dataContext:
          "Only applicable where Police Presence Requested = Yes. Options: To protect people from an immediate risk to life, or being subject to/at risk of serious harm; Not enough staff; Person to be transferred to another place of safety for assessment; Other",
        recordedOn: "eMHA",
      },
      {
        term: "Police Presence Response (Health)",
        definition:
          "Whether police accepted the request and remained at the place of safety when requested as input by the health user.",
        dataContext:
          "The response from the health user as to whether the officer(s) remained or not. User input = Yes/No",
        recordedOn: "eMHA",
      },
      {
        term: "Police Presence Response (Police)",
        definition:
          "Whether police accepted the request and remained at the place of safety when requested as input by the police user.",
        dataContext:
          "The response from the police as to remain or not. Officer input = Yes/No",
        recordedOn: "MHForms",
      },
    ],
  },
  {
    heading: "Outcomes",
    variant: "standard",
    intro:
      "This section defines the incident outcome captured on the Outcome Form and the standard aggregations used for reporting.\n\nThis section applies to S136 Incidents only.",
    rows: [
      {
        term: "Outcome Form Completion %",
        definition:
          "Percentage of Completed S136 incidents that have a recorded health outcome (i.e. an Outcome Form has been completed)",
        dataContext:
          "Used as the standard completeness indicator for outcome based reporting.",
        recordedOn: "Outcome Form",
      },
      {
        term: "Incident Outcome (Police)",
        definition:
          "The recorded outcome for the individual at the point the police form is closed (if known).",
        dataContext:
          'Recorded by the officer on the police form. Selected by Police in response to the question: "Do you know the outcome of the assessment?" -> (if Yes) "What was the outcome of the assessment?" on the Police Form: Discharged; Admitted; Other (e.g. local community care)',
        recordedOn: "MH Forms",
      },
      {
        term: "Incident Outcome (Health)",
        definition:
          "The recorded outcome for the individual at the point the Section 136 incident is closed - i.e., the action taken after the assessment/pathway.",
        dataContext:
          'Recorded by the health user on the outcome form. Selected by Health in response to the question: "Please confirm the outcome of the 136 incident" on the Outcome Form. Options: Section 136 Expired - Legal timeframe elapsed; Admission to a mental health unit under MHA; Admission to a mental health unit informally; Discharge with mental health follow up; Discharge with primary care follow up; Discharge no follow up required; Discharged - taken to custody; Discharged - transferred to physical healthcare unit; Other',
        recordedOn: "Outcome Form",
      },
      {
        term: "Section 136 Expired - Legal timeframe elapsed",
        definition:
          "The legal time limit for the Section 136 detention ran out before the pathway was concluded with admission, discharge, or transfer.",
        dataContext:
          'Selectable Incident Outcome entry within "Please confirm the outcome of the 136 incident".',
        recordedOn: "Outcome Form",
      },
      {
        term: "Admission to a mental health unit under MHA",
        definition:
          "The person was detained to a mental health inpatient unit under Mental Health Act powers (compulsory admission/detention).",
        dataContext:
          'Selectable Incident Outcome entry within "Please confirm the outcome of the 136 incident".',
        recordedOn: "Outcome Form",
      },
      {
        term: "Admission to a mental health unit informally",
        definition:
          "The person was admitted to a mental health inpatient unit voluntarily.",
        dataContext:
          'Selectable Incident Outcome entry within "Please confirm the outcome of the 136 incident".',
        recordedOn: "Outcome Form",
      },
      {
        term: "Discharge with mental health follow up",
        definition:
          "The person was discharged and follow-up with mental health services was arranged.",
        dataContext:
          'Selectable Incident Outcome entry within "Please confirm the outcome of the 136 incident".',
        recordedOn: "Outcome Form",
      },
      {
        term: "Discharge with primary care follow up",
        definition:
          "The person was discharged and follow-up with primary care (e.g., GP) was arranged or advised.",
        dataContext:
          'Selectable Incident Outcome entry within "Please confirm the outcome of the 136 incident".',
        recordedOn: "Outcome Form",
      },
      {
        term: "Discharge no follow up required",
        definition: "The person was discharged and no follow-up was needed.",
        dataContext:
          'Selectable Incident Outcome entry within "Please confirm the outcome of the 136 incident".',
        recordedOn: "Outcome Form",
      },
      {
        term: "Discharged - taken to custody",
        definition:
          "The person was discharged from the health pathway and taken into police custody.",
        dataContext:
          'Selectable Incident Outcome entry within "Please confirm the outcome of the 136 incident".',
        recordedOn: "Outcome Form",
      },
      {
        term: "Discharged - transferred to physical healthcare unit",
        definition:
          "The person was transferred to a physical healthcare service (e.g., ED/acute ward) for physical health needs.",
        dataContext:
          'Selectable Incident Outcome entry within "Please confirm the outcome of the 136 incident".',
        recordedOn: "Outcome Form",
      },
      {
        term: "Other",
        definition: "The outcome doesn't fit any of the outcome options.",
        dataContext:
          'Selectable Incident Outcome entry within "Please confirm the outcome of the 136 incident".',
        recordedOn: "Outcome Form",
      },
      {
        term: "Formal Admission (aggregation)",
        definition:
          "A reporting grouping for outcomes where the person was detained to a mental health unit under the Mental Health Act.",
        dataContext:
          "Derived from Incident Outcome. Includes: Admission to a mental health unit under MHA",
        recordedOn: "Outcome Form",
      },
      {
        term: "Informal Admission (aggregation)",
        definition:
          "A reporting grouping for outcomes where the person was admitted to a mental health unit voluntarily.",
        dataContext:
          "Derived from Incident Outcome. Includes: Admission to a mental health unit informally",
        recordedOn: "Outcome Form",
      },
      {
        term: "Discharged (aggregation)",
        definition:
          "A reporting grouping for outcomes where the person was not admitted to a mental health unit and instead left via discharge, transfer to physical healthcare, or custody.",
        dataContext:
          "Derived from Incident Outcome. Includes: Discharge with mental health follow up; Discharge with primary care follow up; Discharge no follow up required; Discharged - taken to custody; Discharged - transferred to physical healthcare unit",
        recordedOn: "Outcome Form",
      },
    ],
  },
  {
    heading: "Durations",
    variant: "durations",
    intro:
      'This section defines the duration metrics used throughout the dashboards, including how each duration is calculated and the cleaning rules used to hide or discard invalid records for each metric. Duration metrics may include both live and completed incidents where valid timestamps exist, but each duration has its own "valid data" population, so denominators must be applied per metric rather than assumed globally.\n\nDurations are split into two categories:\n\n\u2022 Durations which apply to all incidents\n\u2022 Durations which apply to S136 incidents only',
    rows: [
      {
        term: "Durations which apply to all incidents",
        definition: "",
        calculation: "",
        cleaningRules: "",
        recordedOn: "",
        isGroupHeader: true,
      },
      {
        term: "Police Incident Duration",
        definition:
          "How long the police detention episode lasted, from when the individual was detained by the police to when the officer(s) left.",
        calculation: "dateTimeLeft - dateTimeOfDetention",
        cleaningRules:
          "Hide Incident if: Time of Police Detention is missing; Time of Police Leave is missing; Police Incident Duration <= 15 mins; Police Incident Duration >= 48 hours",
        recordedOn: "Police Form",
      },
      {
        term: "Police Involvement Duration",
        definition:
          "How long the police were involved in the incident from the initial call to the police to officers leaving the final place of safety.",
        calculation: "dateTimeLeft - cadDateTime",
        cleaningRules:
          "Hide Incident if: CAD Time is missing; Time of Police Leave is missing; Police Involvement Duration < 15 mins; Police Involvement Duration > 48 hours; Form was started in error",
        recordedOn: "Police Form (Met and CoLP Only)",
      },
      {
        term: "Total Police POS / ED Duration",
        definition:
          "How long from the police arriving at the first place of safety to the police leaving the final place of safety.",
        calculation: "dateTimeLeft (Final POS) - arrivalDateTime (First POS)",
        cleaningRules:
          "Hide Incident if: Time of Arrival is missing; Time of Police Leave is missing; Entry to Police Leave Duration < 5 mins; Entry to Police Leave Duration > 48 hours; Form was started in error",
        recordedOn: "Police Form",
      },
      {
        term: "Police POS Visit Duration",
        definition:
          "How long the police spends associated with a single POS visit, measured from police arrival at that POS to police arrival at the next POS, or to the police leave time if there is no subsequent POS.",
        calculation:
          "For POS n: If there is a subsequent POS: arrivalDateTime (POS n+1) - arrivalDateTime (POS n); If final POS: dateTimeLeft - arrivalDateTime (POS n)",
        cleaningRules:
          "Hide Incident if: Time of Arrival for POS n is missing; Arrival Time for POS n+1 is missing AND Police Leave Time is missing; Police POS Visit Duration < 5 mins; Police POS Visit Duration > 48 hours; Form was started in error",
        recordedOn: "Police Form",
      },
      {
        term: "Detention to Arrival at First POS Duration",
        definition:
          "How long it took to transport the individual to the first place of safety after being detained by the police.",
        calculation:
          "arrivalDateTime - dateTimeOfDetention (Arrival time is for First POS)",
        cleaningRules:
          "Hide Incident if: Time of Police Detention is missing; Time of Arrival is missing for First POS; Conveyance Duration is negative; Conveyance Duration > 48 hours",
        recordedOn: "Police Form",
      },
      {
        term: "Detention to Arrival at Final POS Duration",
        definition:
          "How long it took to transport the individual to the final place of safety after being detained by the police.",
        calculation:
          "arrivalDateTime - dateTimeOfDetention (Arrival time is for Final POS)",
        cleaningRules:
          "Hide Incident if: Time of Police Detention is missing; Time of Arrival is missing for Final POS; Conveyance Duration is negative; Conveyance Duration > 48 hours",
        recordedOn: "Police Form",
      },
      {
        term: "Durations which apply to S136 incidents only",
        definition: "",
        calculation: "",
        cleaningRules: "",
        recordedOn: "",
        isGroupHeader: true,
      },
      {
        term: "136 Detention Duration",
        definition:
          "How long the person remained legally detained under S136, from the arrival at the first Place of Safety to the incident outcome (informal or compulsory admission under MHA or discharge).",
        calculation: "outcomeDateTime - arrivalDateTime (First POS)",
        cleaningRules:
          "Hide Incident if: Time of Arrival is missing; Time of Outcome is missing; 136 Detention Duration < 15 mins; 136 Detention Duration > 48 hours; Form was started in error",
        recordedOn: "Police Form; Outcome Form",
      },
      {
        term: "Duration from Police Detention to Outcome",
        definition:
          "The time from police detaining the individual to the incident outcome.",
        calculation: "outcomeDateTime - dateTimeOfDetention",
        cleaningRules:
          "Hide Incident if: Time of Police Detention is missing; Outcome Time is missing; Duration from Police Detention to Outcome < 15 mins; Duration from Police Detention to Outcome > 48 hours; Form was started in error",
        recordedOn: "Police Form; Outcome Form",
      },
      {
        term: "Duration of Patient Time Spent in Individual POS under 136 Detention",
        definition:
          "How long the patient spent at a single POS visit, measured from patient arrival at that POS to patient arrival at the next POS, or the outcome time if there is no subsequent POS.",
        calculation:
          "For POS n: If there is a subsequent POS: arrivalDateTime (POS n+1) - arrivalDateTime (POS n); If final POS: outcomeDateTime - arrivalDateTime (POS n)",
        cleaningRules:
          "Hide Incident if: Time of Arrival for POS n is missing; Arrival Time for POS n+1 is missing AND Outcome Time is missing; Patient POS Visit Duration < 5 mins; Patient POS Visit Duration > 48 hours; Form was started in error",
        recordedOn: "Police Form; Outcome Form",
      },
      {
        term: "AMHP Response Duration",
        definition:
          "Date and Time AMHP was contacted to Date and Time AMHP arrived.",
        calculation: "amhparrivalDateTime - amhpcontactDateTime",
        cleaningRules:
          "Hide Incident if: AMHP Contact Time is missing; AMHP Arrival Time is missing; AMHP Response Time < 5 mins; AMHP Response Time > 24 hours",
        recordedOn: "Outcome Form",
      },
      {
        term: "First Doctor Response Duration",
        definition:
          "Date and Time First Doctor was contacted to Date and Time First Doctor arrived.",
        calculation: "firstdrarrivalDateTime - firstdrcontactDateTime",
        cleaningRules:
          "Hide Incident if: First Doctor Contact Time is missing; First Doctor Arrival Time is missing; First Doctor Response Time < 5 mins; First Doctor Response Time > 24 hours",
        recordedOn: "Outcome Form",
      },
      {
        term: "Second Doctor Response Duration",
        definition:
          "Date and Time Second Doctor was contacted to Date and Time Second Doctor arrived.",
        calculation: "seconddrarrivalDateTime - firstdrcontactDateTime",
        cleaningRules:
          "Hide Incident if: Second Doctor Contact Time is missing; First Doctor Arrival Time is missing; Second Doctor Response Time < 5 mins; Second Doctor Response Time > 24 hours",
        recordedOn: "Outcome Form",
      },
      {
        term: "AMHP Contact to Completion Duration",
        definition:
          "The time from when the AMHP was contacted to when the Mental Health Act assessment was completed for the incident.",
        calculation: "assessment_complete_date_time - amhp_contact_date_time",
        cleaningRules:
          "Hide Incident if: AMHP Contact to Completion Duration is missing; AMHP Contact to Completion Duration is missing; AMHP Contact to Completion Duration < 5 mins; AMHP Contact to Completion Duration > 24 hours",
        recordedOn: "Outcome Form",
      },
    ],
  },
];
